import { multiplyCurrency } from "../utils/currencyUtils";

/** Entry-fee splits are charged once per bowler, never once per team. */
export const EVENT_FEES_LABEL = "Fees (per bowler)";
export const PRIZE_FUND_FEE_NAME = "Prize Fund";
export const FEE_SPLIT_HELP =
  "Each amount is per bowler, not the total for the board.";
export const SAMPLE_BOWLER_COUNT = 40;

export const isPrizeFundFee = (feeName) => feeName === PRIZE_FUND_FEE_NAME;

export const perBowlerFeeLabel = (feeName) => `${feeName} per bowler`;

export const formatPrizeFundMoney = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "";
  const whole = Math.abs(n - Math.round(n)) < 0.001;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
};

export const parsePerBowlerRate = (value) => {
  if (value == null || String(value).trim() === "") return null;
  const rate = Number(value);
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return rate;
};

export const bowlerCountOrZero = (count) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.floor(n);
};

/**
 * Board total for a per-bowler prize-fund rate.
 * Returns null when the rate is blank or not a positive number.
 * A count of 0 is a real total of $0 (no bowlers yet), not a missing rate.
 */
export const estimatedPrizeFundTotal = (perBowlerAmount, bowlerCount) => {
  const rate = parsePerBowlerRate(perBowlerAmount);
  if (rate == null) return null;
  return multiplyCurrency(rate, bowlerCountOrZero(bowlerCount));
};

const toEstimateEvent = (event) => ({
  name: event?.name || event?.eventName || "",
  bowlerCount: event?.bowlerCount ?? 0,
  bowlerQuantity: Number(event?.bowlerQuantity) || 1,
});

/** Squad templates carry one row per spawned event. Other events are themselves. */
export const estimateEventsFrom = (eventOrEvents) => {
  const list = Array.isArray(eventOrEvents) ? eventOrEvents : [eventOrEvents];
  const expanded = [];
  list.filter(Boolean).forEach((event) => {
    if (Array.isArray(event.estimateEvents) && event.estimateEvents.length > 0) {
      event.estimateEvents.forEach((squad) => {
        expanded.push(toEstimateEvent(squad));
      });
      return;
    }
    expanded.push(toEstimateEvent(event));
  });
  return expanded.length > 0
    ? expanded
    : [{ name: "", bowlerCount: 0, bowlerQuantity: 1 }];
};

const bowlerWord = (count) => (count === 1 ? "bowler" : "bowlers");

const teamSizedNote = (events) =>
  events.some((event) => Number(event.bowlerQuantity) > 1)
    ? "Doubles and team events still use this rate per bowler, not per team."
    : "";

/**
 * Lines shown under the Prize Fund input.
 * When every count is 0, the fallback says the typed rate is not a board total
 * and gives a worked example at SAMPLE_BOWLER_COUNT bowlers.
 */
export const prizeFundEstimateLines = (perBowlerAmount, events) => {
  const rate = parsePerBowlerRate(perBowlerAmount);
  if (rate == null) return [];

  const list = estimateEventsFrom(events);
  const counted = list
    .map((event) => ({
      ...event,
      bowlers: bowlerCountOrZero(event.bowlerCount),
    }))
    .filter((event) => event.bowlers > 0);
  const note = teamSizedNote(list);

  if (counted.length === 0) {
    const sampleTotal = multiplyCurrency(rate, SAMPLE_BOWLER_COUNT);
    return [
      `No bowlers entered yet. ${formatPrizeFundMoney(rate)} is per bowler, not the board total.`,
      `Example: ${formatPrizeFundMoney(rate)} × ${SAMPLE_BOWLER_COUNT} bowlers = ${formatPrizeFundMoney(sampleTotal)}.`,
      note,
    ].filter(Boolean);
  }

  const lines = counted.map((event) => {
    const total = multiplyCurrency(rate, event.bowlers);
    const prefix = counted.length > 1 && event.name ? `${event.name}: ` : "";
    return `${prefix}${formatPrizeFundMoney(rate)} × ${event.bowlers} ${bowlerWord(event.bowlers)} = ${formatPrizeFundMoney(total)} estimated prize fund`;
  });
  if (note) lines.push(note);
  return lines;
};
